import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { aboutContent, groupCompanies } from '../data';

const About = () => {
  const [ref, isVisible] = useScrollAnimation();

  const content = aboutContent || {};
  const companies = groupCompanies || [];

  const stats = [
    { number: '50+', label: 'Years Combined Experience' },
    { number: '1500+', label: 'Satisfied Clients' },
    { number: '200+', label: 'Expert Team Members' },
    { number: '100+', label: 'Global Premium Clients' }
  ];

  return (
    <section className="section-padding bg-gray-50" ref={ref}>
      <div className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-hover">
              <img 
                src={content.src || 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=600&q=80'} 
                alt="About Al Abeer Diamond"
                className="w-full h-auto"
              />
            </div>
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-primary/10 rounded-full -z-10"></div>
            <div className="absolute -top-4 -right-4 w-16 h-16 bg-primary/20 rounded-full -z-10"></div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isVisible ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="section-badge mb-4">About Us</span>
            <h2 className="heading-primary mb-6">
              {content.headingH1 || 'Welcome to '}
              <span className="text-gradient">{content.span || 'Al Abeer Diamond'}</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {content.para || 'With a strong foundation built on technical excellence and customer-focused service...'}
            </p>
            <p className="text-gray-600 leading-relaxed mb-4">
              {content.para2 || 'Over the years, we have broadened our capabilities...'}
            </p>
            <p className="text-gray-600 leading-relaxed mb-8">
              {content.para3 || 'Today, Al Abeer stands as a trusted partner...'}
            </p>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl md:text-3xl font-bold text-primary font-oswald">
                    {stat.number}
                  </div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>

            <Link to="/about" className="btn-primary inline-block">
              Learn More
            </Link>
          </motion.div>
        </div>

        {/* Group Companies Section */}
        {companies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isVisible ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-16 pt-16 border-t border-gray-200"
          >
            <div className="text-center mb-10">
              <span className="section-badge">Our Network</span>
              <h3 className="text-2xl md:text-3xl font-bold text-secondary font-oswald mt-4">
                Group of <span className="text-gradient">Companies</span>
              </h3>
              <p className="text-gray-600 max-w-2xl mx-auto mt-3">
                We operate as part of a multinational group with a strong global presence
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {companies.map((company, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isVisible ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.05 + 0.5 }}
                  className="bg-white rounded-xl p-4 text-center shadow-soft hover:shadow-hover transition-all hover:-translate-y-1 border border-gray-100 flex flex-col items-center"
                >
                  <div className="w-20 h-20 flex items-center justify-center mb-3 bg-gray-50 rounded-lg p-2">
                    {company.logo ? (
                      <img
                        src={company.logo}
                        alt={company.name}
                        className="max-w-full max-h-full object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <i className="uil uil-building text-primary text-2xl"></i>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 font-medium leading-tight">
                    {company.name}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default About;